// Voorkeuren per activiteit: favorieten en (tijdelijk of blijvend) verborgen activiteiten.
// Opslag: localStorage-sleutel KEYS.prefs ('gameon_prefs_v1'), JSON van `Preferences`.

export interface Preferences {
  /** id's van favoriete activiteiten (uniek, positief geheel). */
  favorites: number[];
  /** id → tijdstip (epoch ms) tot wanneer verborgen. HIDDEN_FOREVER = blijvend. */
  hiddenUntil: Record<string, number>;
}

export const HIDDEN_FOREVER = Number.MAX_SAFE_INTEGER;
export const HIDE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;
export const EMPTY_PREFERENCES: Preferences = { favorites: [], hiddenUntil: {} };

function validId(v: unknown): number | null {
  const n = typeof v === 'string' && /^\d+$/.test(v.trim()) ? Number(v) : v;
  return typeof n === 'number' && Number.isSafeInteger(n) && n > 0 ? n : null;
}

/** Veilig parsen van de bewaarde waarde (string of object); ongeldige delen worden genegeerd. */
export function parsePreferences(raw: unknown): Preferences {
  let v: unknown = raw;
  if (typeof v === 'string') {
    try {
      v = JSON.parse(v);
    } catch {
      return { favorites: [], hiddenUntil: {} };
    }
  }
  if (!v || typeof v !== 'object' || Array.isArray(v)) return { favorites: [], hiddenUntil: {} };
  const o = v as Record<string, unknown>;
  const hiddenUntil: Record<string, number> = {};
  if (o.hiddenUntil && typeof o.hiddenUntil === 'object' && !Array.isArray(o.hiddenUntil)) {
    for (const [k, val] of Object.entries(o.hiddenUntil as Record<string, unknown>)) {
      const id = validId(k);
      if (id !== null && typeof val === 'number' && Number.isFinite(val) && val > 0) hiddenUntil[String(id)] = val;
    }
  }
  const favorites: number[] = [];
  if (Array.isArray(o.favorites)) {
    for (const f of o.favorites) {
      const id = validId(f);
      if (id !== null && !favorites.includes(id) && !(String(id) in hiddenUntil)) favorites.push(id);
    }
  }
  return { favorites, hiddenUntil };
}

export function toggleFavorite(prefs: Preferences, id: number): Preferences {
  if (validId(id) === null) return prefs;
  if (prefs.favorites.includes(id)) return { ...prefs, favorites: prefs.favorites.filter((f) => f !== id) };
  return { ...prefs, favorites: [...prefs.favorites, id] };
}

/** until = HIDDEN_FOREVER of now + HIDE_WEEK_MS. Een verborgen activiteit is geen favoriet meer. */
export function hideActivity(prefs: Preferences, id: number, until: number): Preferences {
  if (validId(id) === null || !Number.isFinite(until)) return prefs;
  return {
    favorites: prefs.favorites.filter((f) => f !== id),
    hiddenUntil: { ...prefs.hiddenUntil, [String(id)]: until },
  };
}

export function unhideActivity(prefs: Preferences, id: number): Preferences {
  const key = String(id);
  if (!(key in prefs.hiddenUntil)) return prefs;
  const hiddenUntil = { ...prefs.hiddenUntil };
  delete hiddenUntil[key];
  return { ...prefs, hiddenUntil };
}

export function isHidden(prefs: Preferences, id: number, now: number): boolean {
  const until = prefs.hiddenUntil[String(id)];
  return typeof until === 'number' && until > now;
}

/** Ruimt verlopen verbergingen op en verwijdert id's die niet meer bestaan (`existingIds`). */
export function prunePreferences(prefs: Preferences, now: number, existingIds: ReadonlySet<number>): Preferences {
  const hiddenUntil: Record<string, number> = {};
  for (const [k, until] of Object.entries(prefs.hiddenUntil)) {
    if (until > now && existingIds.has(Number(k))) hiddenUntil[k] = until;
  }
  const favorites = prefs.favorites.filter((f) => existingIds.has(f));
  const same =
    favorites.length === prefs.favorites.length &&
    Object.keys(hiddenUntil).length === Object.keys(prefs.hiddenUntil).length;
  return same ? prefs : { favorites, hiddenUntil };
}
